import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fetchConsumoIa } from '../../../api/iaConsumo';
import './ChatGemini.css';

function SugestaoBaseConhecimento({ sugestao, apiBaseUrl, getAuthHeader }) {
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(!!sugestao.gravado);

  if (!sugestao || !sugestao.titulo) return null;

  if (sugestao.gravado === true) {
    return (
      <div className="chat-gemini-sugestao-base mt-2 small text-success">
        <i className="fas fa-check-circle" /> Guardado na base de conhecimento: &quot;{sugestao.titulo}&quot;
      </div>
    );
  }

  if (sugestao.gravado === false && sugestao.erro) {
    return (
      <div className="chat-gemini-sugestao-base mt-2 small text-danger">
        <i className="fas fa-exclamation-triangle" /> {sugestao.erro}
      </div>
    );
  }

  if (salvo) {
    return (
      <div className="chat-gemini-sugestao-base mt-2 small text-success">
        <i className="fas fa-check-circle" /> Salvo na base de conhecimento.
      </div>
    );
  }

  const salvarNaBase = async () => {
    if (!apiBaseUrl || salvando) return;
    setSalvando(true);
    try {
      const url = apiBaseUrl.replace(/\/$/, '') + '/base-conhecimento';
      await axios.post(
        url,
        {
          titulo: sugestao.titulo,
          conteudo: sugestao.conteudo,
          categoria: sugestao.categoria || undefined,
        },
        { headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, timeout: 15000 }
      );
      setSalvo(true);
    } catch (err) {
      console.error('Erro ao salvar na base:', err);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="chat-gemini-sugestao-base mt-2">
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={salvarNaBase}
        disabled={salvando}
        title="Adicionar esta sugestão à base de conhecimento"
      >
        {salvando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-book" />}
        {salvando ? ' Salvando...' : ' Salvar na base de conhecimento'}
      </button>
    </div>
  );
}

const CHAT_STORAGE_PREFIX = 'tacoma_chat_';
const CHAT_STORAGE_MAX = 200;

function getStorageKey(role) {
  return CHAT_STORAGE_PREFIX + (role || 'analista');
}

function loadMensagensFromStorage(role) {
  try {
    const raw = localStorage.getItem(getStorageKey(role));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-CHAT_STORAGE_MAX) : [];
  } catch {
    return [];
  }
}

function saveMensagensToStorage(role, mensagens) {
  try {
    const key = getStorageKey(role);
    if (mensagens.length === 0) {
      localStorage.removeItem(key);
    } else {
      const toSave = mensagens.slice(-CHAT_STORAGE_MAX);
      localStorage.setItem(key, JSON.stringify(toSave));
    }
  } catch (e) {
    console.warn('Chat: falha ao salvar conversa no navegador.', e);
  }
}

function ChatGemini({ apiBaseUrl, chatPath, role = 'analista', descricaoChamado = '', tramites = [] }) {
  const [mensagens, setMensagens] = useState(() => loadMensagensFromStorage(role));
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [consumo, setConsumo] = useState(null);
  const listaRef = useRef(null);

  useEffect(() => {
    if (listaRef.current) listaRef.current.scrollTop = listaRef.current.scrollHeight;
  }, [mensagens]);

  useEffect(() => {
    if (!apiBaseUrl || !chatPath) return;
    fetchConsumoIa(apiBaseUrl, chatPath).then((data) => {
      if (data) setConsumo(data);
    });
  }, [apiBaseUrl, chatPath]);

  useEffect(() => {
    saveMensagensToStorage(role, mensagens);
  }, [role, mensagens]);

  const apagarConversa = useCallback(() => {
    setMensagens([]);
    setErro('');
    saveMensagensToStorage(role, []);
  }, [role]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) return {};
    return { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` };
  };

  const enviar = async () => {
    const msg = (texto || '').trim();
    if (!msg || enviando) return;
    if (!apiBaseUrl || !chatPath) {
      setErro('Backend não configurado.');
      return;
    }

    setErro('');
    setTexto('');
    setMensagens((prev) => [...prev, { tipo: 'usuario', texto: msg }]);
    setEnviando(true);

    const historicoParaBackend = mensagens
      .slice(-30)
      .map((m) => ({ tipo: m.tipo, texto: m.texto || '' }));

    try {
      const url = apiBaseUrl.replace(/\/$/, '') + chatPath;
      const body = {
        mensagem: msg,
        role,
        ...(historicoParaBackend.length > 0 && { historicoConversa: historicoParaBackend }),
        ...(descricaoChamado && { descricaoChamado }),
        ...(tramites && tramites.length > 0 && { tramites }),
      };
      const res = await axios.post(url, body, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        timeout: 90000,
      });
      const resposta = res.data?.resposta || 'Sem resposta.';
      const chamados = res.data?.chamadosUtilizados || [];
      const sugestaoBase = res.data?.sugestaoBaseConhecimento || null;
      setMensagens((prev) => [
        ...prev,
        {
          tipo: 'assistente',
          texto: resposta,
          chamadosUtilizados: chamados,
          sugestaoBaseConhecimento: sugestaoBase,
        },
      ]);
      if (consumo) {
        setConsumo((c) => (c ? { ...c, creditos_usados: (c.creditos_usados || 0) + 1 } : c));
      }
    } catch (err) {
      const msgErro = err.response?.data?.error || err.message || 'Erro ao consultar o assistente. Tente novamente.';
      const isLimiteAtingido = err.response?.status === 429 ||
        (err.response?.status === 403 && (msgErro || '').toLowerCase().includes('limite'));
      setErro(msgErro);
      if (!isLimiteAtingido) {
        setMensagens((prev) => [
          ...prev,
          { tipo: 'assistente', texto: `Não foi possível obter resposta: ${msgErro}`, erro: true },
        ]);
      }
    } finally {
      setEnviando(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <div className="chat-gemini">
      {consumo && role !== 'solicitante' && (
        <div className="chat-gemini-uso mb-2 small text-muted">
          {consumo.creditos_limite != null && consumo.creditos_limite > 0
            ? `Mensagens usadas: ${consumo.creditos_usados ?? 0} / ${consumo.creditos_limite}${consumo.ano_mes ? ` (${consumo.ano_mes})` : ''}`
            : `Mensagens usadas: ${consumo.creditos_usados ?? 0} (sem limite)${consumo.ano_mes ? ` — ${consumo.ano_mes}` : ''}`}
        </div>
      )}
      <div className="chat-gemini-lista" ref={listaRef}>
        {mensagens.length === 0 && (
          <div className="chat-gemini-vazio">
            <p className="chat-gemini-vazio-titulo">Fale com o Tacoma</p>
            <p className="chat-gemini-vazio-desc">
              Faça perguntas sobre procedimentos, erros ou dúvidas sobre os sistemas Softbridge.
            </p>
          </div>
        )}
        {mensagens.map((m, i) => (
          <div key={i} className={`chat-gemini-msg chat-gemini-msg--${m.tipo}`}>
            <div className="chat-gemini-msg-conteudo">
              {m.tipo === 'assistente' && <span className="chat-gemini-msg-role">Tacoma</span>}
              <div className="chat-gemini-msg-texto">{m.texto}</div>
              {m.chamadosUtilizados && m.chamadosUtilizados.length > 0 && (
                <div className="chat-gemini-chamados">
                  Referência: chamados #{m.chamadosUtilizados.map((c) => c.satNum).join(', #')}
                </div>
              )}
              {m.sugestaoBaseConhecimento && (
                <SugestaoBaseConhecimento
                  sugestao={m.sugestaoBaseConhecimento}
                  apiBaseUrl={apiBaseUrl}
                  getAuthHeader={getAuthHeader}
                />
              )}
            </div>
          </div>
        ))}
        {enviando && (
          <div className="chat-gemini-msg chat-gemini-msg--assistente">
            <div className="chat-gemini-msg-conteudo">
              <span className="chat-gemini-msg-role">Tacoma</span>
              <div className="chat-gemini-msg-texto chat-gemini-digitando">
                <span className="chat-gemini-dots">...</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {erro && <div className="chat-gemini-erro">{erro}</div>}
      {mensagens.length > 0 && (
        <div className="chat-gemini-toolbar">
          <button
            type="button"
            className="chat-gemini-apagar btn btn-sm btn-outline-secondary"
            onClick={apagarConversa}
            disabled={enviando}
            title="Apagar conversa (a conversa só some quando você clica aqui)"
          >
            <i className="fas fa-trash-alt" /> Apagar conversa
          </button>
        </div>
      )}
      <div className="chat-gemini-input-wrap">
        <textarea
          className="chat-gemini-input"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Digite sua pergunta..."
          rows={2}
          disabled={enviando}
        />
        <button
          type="button"
          className="chat-gemini-enviar btn btn-primary"
          onClick={enviar}
          disabled={enviando || !texto.trim()}
        >
          {enviando ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-paper-plane" />}
          <span>Enviar</span>
        </button>
      </div>
    </div>
  );
}

export default ChatGemini;
